import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../services/supabase';
import RimInventoryCard, { RimInventoryItem } from './cards/RimInventoryCard';

interface RimInventorySectionProps {
  businessId: string;
  businessName?: string;
}

const RimInventorySection: React.FC<RimInventorySectionProps> = ({
  businessId,
}) => {
  const [inventory, setInventory] = useState<RimInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedBoltPattern, setSelectedBoltPattern] = useState('');
  const [selectedFinish, setSelectedFinish] = useState('');
  const [selectedStockStatus, setSelectedStockStatus] = useState('all');

  // Fetch rim inventory
  useEffect(() => {
    const fetchInventory = async () => {
      if (!businessId) return;
      
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('rim_inventory')
          .select('*')
          .eq('business_id', businessId)
          .order('brand', { ascending: true });

        if (fetchError) {
          setError('Failed to load rim inventory');
          console.error('Rim inventory fetch error:', fetchError);
        } else {
          setInventory(data || []);
        }
      } catch (err) {
        setError('An unexpected error occurred');
        console.error('Rim inventory fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [businessId]);

  // Generate filter options from inventory data
  const filterOptions = useMemo(() => {
    const brands = [...new Set(inventory.map(item => item.brand))].sort();
    const sizes = [...new Set(inventory.map(item => item.diameter?.toString() || ''))].filter(Boolean).sort((a, b) => Number(a) - Number(b));
    const boltPatterns = [...new Set(inventory.map(item => item.bolt_pattern || ''))].filter(Boolean).sort();
    const finishes = [...new Set(inventory.map(item => item.finish || ''))].filter(Boolean).sort();

    return { brands, sizes, boltPatterns, finishes };
  }, [inventory]);

  // Filter and search inventory
  const filteredInventory = useMemo(() => {
    let filtered = inventory;

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.brand.toLowerCase().includes(query) ||
        item.model.toLowerCase().includes(query) ||
        item.size.toLowerCase().includes(query) ||
        item.finish?.toLowerCase().includes(query) ||
        item.bolt_pattern?.toLowerCase().includes(query)
      );
    }

    // Apply filters
    if (selectedBrand) {
      filtered = filtered.filter(item => item.brand === selectedBrand);
    }

    // Zero-stock items are never shown to customers
    filtered = filtered.filter(item => item.quantity > 0);

    if (selectedSize) {
      filtered = filtered.filter(item => item.diameter?.toString() === selectedSize);
    }

    if (selectedBoltPattern) {
      filtered = filtered.filter(item => item.bolt_pattern === selectedBoltPattern);
    }

    if (selectedFinish) {
      filtered = filtered.filter(item => item.finish === selectedFinish);
    }

    if (selectedStockStatus !== 'all') {
      filtered = filtered.filter(item => {
        switch (selectedStockStatus) {
          case 'in_stock':
            return item.quantity > 3;
          case 'low_stock':
            return item.quantity > 0 && item.quantity <= 3;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [inventory, searchQuery, selectedBrand, selectedSize, selectedBoltPattern, selectedFinish, selectedStockStatus]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedBrand('');
    setSelectedSize('');
    setSelectedBoltPattern('');
    setSelectedFinish('');
    setSelectedStockStatus('all');
  };

  // Don't render section if no inventory exists
  if (!loading && inventory.length === 0) {
    return null;
  }

  return (
    <section className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Premium Rims & Wheels
          </h2>
          <p className="text-gray-600">
            Browse our selection of high-quality rims
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by brand, model, size, or finish..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">Filters</h3>
            {(selectedBrand || selectedSize || selectedBoltPattern || selectedFinish || selectedStockStatus !== 'all') && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear All
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Brand Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Brands</option>
                {filterOptions.brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
            
            {/* Size Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
              <select
                value={selectedSize}
                onChange={(e) => setSelectedSize(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Sizes</option>
                {filterOptions.sizes.map(size => (
                  <option key={size} value={size}>{size}"</option>
                ))}
              </select>
            </div>
            
            {/* Bolt Pattern Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bolt Pattern</label>
              <select
                value={selectedBoltPattern}
                onChange={(e) => setSelectedBoltPattern(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Patterns</option>
                {filterOptions.boltPatterns.map(pattern => (
                  <option key={pattern} value={pattern}>{pattern}</option>
                ))}
              </select>
            </div>
            
            {/* Finish Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Finish</label>
              <select
                value={selectedFinish}
                onChange={(e) => setSelectedFinish(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Finishes</option>
                {filterOptions.finishes.map(finish => (
                  <option key={finish} value={finish}>{finish}</option>
                ))}
              </select>
            </div>

            {/* Stock Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stock Status</label>
              <select
                value={selectedStockStatus}
                onChange={(e) => setSelectedStockStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Stock</option>
                <option value="in_stock">In Stock</option>
                <option value="low_stock">Low Stock</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading rim inventory...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Results Count */}
        {!loading && filteredInventory.length > 0 && (
          <div className="mb-4 text-sm text-gray-600">
            Showing {filteredInventory.length} of {inventory.length} rim{inventory.length !== 1 ? 's' : ''}
          </div>
        )}

        {/* Inventory Grid */}
        {filteredInventory.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">
              {searchQuery || selectedBrand || selectedSize || selectedBoltPattern || selectedFinish || selectedStockStatus !== 'all'
                ? 'No rims match your search criteria.'
                : 'No rims available.'
              }
            </p>
            {(searchQuery || selectedBrand || selectedSize || selectedBoltPattern || selectedFinish || selectedStockStatus !== 'all') && (
              <button
                onClick={clearFilters}
                className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInventory.map((rim) => (
              <RimInventoryCard key={rim.id} rim={rim} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default RimInventorySection;
